import { useState, useEffect, useCallback, useRef } from "react";
import { api, formatApiError, API_BASE } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Send, Paperclip, History, Loader2, FileSignature, Eye, Printer, Download, Settings2, PenLine, Users } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import ReviewDialog from "../approvals/ReviewDialog";
import CompanySettingsDialog from "./CompanySettingsDialog";
import SpkBuilder from "./SpkBuilder";
import { idr, fmtDateTime } from "../../lib/format";
import { cn } from "../../lib/utils";

const SPK_STATUS = {
  draft: { label: "Draft", cls: "bg-muted text-muted-foreground" },
  pending_approval: { label: "Pending approval", cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
  approved: { label: "Approved", cls: "bg-[#10B981]/10 text-[#10B981]" },
  rejected: { label: "Rejected", cls: "bg-[#E31E24]/10 text-[#E31E24]" },
};

const PARTY_FIELDS = [["name", "Nama"], ["company", "Perusahaan"], ["position", "Jabatan"], ["address", "Alamat"]];

function PartyEditDialog({ doc, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: doc.title || "",
    party1: { name: "", company: "", position: "", address: "", ...(doc.party1 || {}) },
    party2: { name: "", company: "", position: "", address: "", ...(doc.party2 || {}) },
  });
  const [saving, setSaving] = useState(false);

  const setParty = (key, field, value) => setForm((f) => ({ ...f, [key]: { ...f[key], [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/spk/${doc.id}/parties`, form);
      toast.success("Party data updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const partyBlock = (key, label) => (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#1B2A6B] dark:text-blue-300">{label}</p>
      {PARTY_FIELDS.map(([field, flabel]) => (
        <div key={field} className="grid grid-cols-[92px_1fr] items-center gap-2">
          <span className="text-xs text-muted-foreground">{flabel}</span>
          <input data-testid={`party-${key}-${field}-input`} value={form[key][field]}
            onChange={(e) => setParty(key, field, e.target.value)}
            className="h-8 px-2.5 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
        </div>
      ))}
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg bg-background max-h-[90vh] overflow-y-auto" data-testid="party-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Users size={16} className="text-[#1B2A6B] dark:text-blue-300" />Edit Party Data</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-[92px_1fr] items-center gap-2">
            <span className="text-xs text-muted-foreground">Judul SPK</span>
            <input data-testid="party-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="h-8 px-2.5 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
          </div>
          {partyBlock("party1", "Pihak Pertama (Pemberi Tugas)")}
          {partyBlock("party2", "Pihak Kedua (Penerima Tugas)")}
          <p className="text-[11px] text-muted-foreground">Identical repeated values are automatically shown only once in the PDF.</p>
        </div>
        <DialogFooter>
          <button data-testid="party-edit-cancel" onClick={onClose}
            className="h-8 px-3 text-sm rounded-md border border-border hover:bg-muted transition-colors">Batal</button>
          <button data-testid="party-edit-save" onClick={save} disabled={saving}
            className="h-8 px-4 flex items-center gap-1.5 text-sm font-medium rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white transition-colors disabled:opacity-60">
            {saving && <Loader2 size={13} className="animate-spin" />}Simpan
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SpkPreviewDialog({ doc, onClose }) {
  const url = `${API_BASE}/spk/${doc.id}/pdf`;
  const frameRef = useRef(null);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl bg-background h-[88vh] flex flex-col" data-testid="spk-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {doc.title}
            <span className="text-xs font-normal text-muted-foreground">v{doc.version || 1}</span>
          </DialogTitle>
        </DialogHeader>
        <iframe ref={frameRef} src={url} title="SPK preview" className="flex-1 w-full rounded-md border border-border bg-white" data-testid="spk-preview-frame" />
        <DialogFooter className="gap-2">
          <button data-testid="spk-preview-close" onClick={onClose}
            className="h-8 px-3 text-sm rounded-md border border-border hover:bg-muted transition-colors">
            Close
          </button>
          <button data-testid="spk-preview-print" onClick={() => frameRef.current?.contentWindow?.print()}
            className="h-8 px-3 flex items-center gap-1.5 text-sm rounded-md border border-border hover:bg-muted transition-colors">
            <Printer size={14} />Print
          </button>
          <a data-testid="spk-preview-download" href={`${url}?download=1`} download
            className="h-8 px-3 flex items-center gap-1.5 text-sm rounded-md bg-[#1B2A6B] text-white hover:bg-[#16224f] transition-colors">
            <Download size={14} />Download PDF
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SpkSection({ project }) {
  const { user } = useAuth();
  const { users } = useData();
  const [docs, setDocs] = useState(null);
  const [dialog, setDialog] = useState(null); // "create" | doc object (edit)
  const [reviewDoc, setReviewDoc] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [partyDoc, setPartyDoc] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const canManage = user?.role === "admin" || project.created_by === user?.id;

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${project.id}/spk`);
    setDocs(data);
  }, [project.id]);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, [load]);

  const remove = async (doc) => {
    if (!window.confirm(`Delete SPK "${doc.title}"?`)) return;
    try {
      await api.delete(`/spk/${doc.id}`);
      toast.success("SPK deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitForApproval = async (doc) => {
    try {
      await api.post(`/spk/${doc.id}/submit`);
      toast.success("SPK submitted for approval");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const review = async ({ action, note, signaturePath }) => {
    await api.post(`/spk/${reviewDoc.id}/review`, { action, note, signature_path: signaturePath });
    toast.success(`SPK ${action === "approve" ? "signed" : "rejected"}`);
    load();
  };

  const uploadAttachment = async (doc, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/spk/${doc.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const canSign = (doc) => {
    const already = (doc.signatures || []).some((s) => s.by_id === user?.id);
    if (already) return false;
    return (doc.required_signers || []).length ? doc.required_signers.includes(user?.id) : canManage;
  };

  return (
    <div className="space-y-4" data-testid="spk-section">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-heading text-base font-semibold flex items-center gap-2"><FileSignature size={16} className="text-[#1B2A6B] dark:text-blue-300" />SPK Documents</h3>
        <div className="ml-auto flex items-center gap-2">
          {user?.role === "admin" && (
            <button data-testid="company-settings-button" onClick={() => setSettingsOpen(true)}
              className="h-8 px-3 flex items-center gap-1.5 text-sm rounded-md border border-border hover:bg-muted transition-colors">
              <Settings2 size={14} />Company
            </button>
          )}
          <button data-testid="spk-add-button" onClick={() => setDialog("create")}
            className="h-8 px-3 flex items-center gap-1.5 text-sm font-medium rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white transition-colors">
            <Plus size={15} />New SPK
          </button>
        </div>
      </div>

      {docs === null ? (
        <div className="flex justify-center py-10"><div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
      ) : docs.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-lg" data-testid="spk-empty">
          <FileSignature size={26} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">No SPK documents yet. Create the first work agreement for this project.</p>
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {docs.map((doc) => {
            const st = SPK_STATUS[doc.status] || SPK_STATUS.draft;
            const sigTotal = (doc.required_signers || []).length;
            const sigCount = (doc.signatures || []).length;
            const canEdit = doc.status !== "approved" && (canManage || doc.created_by === user?.id);
            return (
              <div key={doc.id} data-testid={`spk-card-${doc.id}`} className="rounded-lg border border-border bg-card p-4 space-y-2.5">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{doc.title}
                      <span className="ml-1.5 text-[10px] font-medium text-muted-foreground" data-testid={`spk-version-${doc.id}`}>v{doc.version || 1}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">{doc.spk_number || "No number"} · {fmtDateTime(doc.created_at)}</p>
                  </div>
                  <span className={cn("text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 shrink-0", st.cls)} data-testid={`spk-status-${doc.id}`}>{st.label}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span>Value: <span className="font-semibold text-foreground">{idr(doc.contract_value)}</span></span>
                  <span>Client: {doc.client_name || "-"}</span>
                  <span>Vendor: {doc.vendor_name || "-"}</span>
                  <span>{doc.start_date || "?"} → {doc.end_date || "?"}</span>
                  {(doc.rab_items || []).length > 0 && (
                    <span className="col-span-2 text-[11px]" data-testid={`spk-totals-${doc.id}`}>
                      Subtotal {idr(doc.rab_subtotal)} · PPN {idr(doc.tax_amount)} · <span className="font-semibold text-foreground">Grand Total {idr(doc.grand_total ?? doc.contract_value)}</span>{doc.commitment_fee ? ` · Fee ${idr(doc.commitment_fee)}` : ""}
                    </span>
                  )}
                </div>
                {sigTotal > 0 && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid={`spk-sign-progress-${doc.id}`}>
                    <PenLine size={11} />{sigCount}/{sigTotal} signed
                  </p>
                )}
                {doc.status === "approved" && (
                  <div className="rounded-md border border-[#10B981]/40 bg-[#10B981]/5 p-2.5 space-y-1" data-testid={`spk-approved-band-${doc.id}`}>
                    <p className="text-xs text-[#10B981] font-semibold">Approved by {doc.reviewed_by_name} · {fmtDateTime(doc.reviewed_at)}</p>
                    {doc.review_note && <p className="text-xs text-muted-foreground italic">"{doc.review_note}"</p>}
                    <div className="flex gap-2 flex-wrap">
                      {(doc.signatures || []).map((s) => (
                        <img key={s.by_id} src={`${API_BASE}/files/${s.signature_path}`} alt={`Signature ${s.by_name}`} title={`${s.by_name} (${s.role})`}
                          className="h-9 rounded border border-border bg-white" data-testid={`spk-signature-${doc.id}-${s.by_id}`} />
                      ))}
                      {!doc.signatures?.length && doc.signature_path && (
                        <img src={`${API_BASE}/files/${doc.signature_path}`} alt="Approver signature" className="h-9 rounded border border-border bg-white" data-testid={`spk-signature-${doc.id}`} />
                      )}
                    </div>
                  </div>
                )}
                <div className="space-y-1">
                  {(doc.attachments || []).map((a) => (
                    <a key={a.id} href={`${API_BASE}/files/${a.storage_path}`} target="_blank" rel="noreferrer" data-testid={`spk-attachment-${a.id}`}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors truncate">
                      <Paperclip size={11} className="shrink-0" />{a.filename}
                    </a>
                  ))}
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-[#1B2A6B] cursor-pointer transition-colors w-fit" data-testid={`spk-upload-${doc.id}`}>
                    <Paperclip size={11} />Attach document
                    <input type="file" className="hidden" onChange={(e) => uploadAttachment(doc, e)} />
                  </label>
                </div>
                <div className="flex items-center gap-1.5 pt-1 border-t border-border flex-wrap">
                  <button data-testid={`spk-preview-${doc.id}`} onClick={() => setPreviewDoc(doc)} className="h-7 px-2 flex items-center gap-1 text-xs rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                    <Eye size={12} />Preview
                  </button>
                  <a data-testid={`spk-download-${doc.id}`} href={`${API_BASE}/spk/${doc.id}/pdf?download=1`} download
                    className="h-7 px-2 flex items-center gap-1 text-xs rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                    <Download size={12} />PDF
                  </a>
                  {canEdit && (
                    <button data-testid={`spk-edit-${doc.id}`} onClick={() => setDialog(doc)} className="h-7 px-2 flex items-center gap-1 text-xs rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                      <Pencil size={12} />Edit
                    </button>
                  )}
                  {canEdit && (
                    <button data-testid={`spk-party-edit-${doc.id}`} onClick={() => setPartyDoc(doc)} className="h-7 px-2 flex items-center gap-1 text-xs rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                      <Users size={12} />Party Data
                    </button>
                  )}
                  {(doc.status === "draft" || doc.status === "rejected") && (canManage || doc.created_by === user?.id) && (
                    <button data-testid={`spk-submit-${doc.id}`} onClick={() => submitForApproval(doc)} className="h-7 px-2 flex items-center gap-1 text-xs rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                      <Send size={12} />Submit
                    </button>
                  )}
                  {doc.status === "pending_approval" && canSign(doc) && (
                    <button data-testid={`spk-review-${doc.id}`} onClick={() => setReviewDoc(doc)} className="h-7 px-2 flex items-center gap-1 text-xs font-medium rounded-md bg-[#1B2A6B] text-white hover:bg-[#16224f] transition-colors">
                      <PenLine size={12} />Sign / Review
                    </button>
                  )}
                  <button data-testid={`spk-history-${doc.id}`} onClick={() => setHistoryFor(historyFor === doc.id ? null : doc.id)} className="h-7 px-2 flex items-center gap-1 text-xs rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                    <History size={12} />History
                  </button>
                  {canManage && (
                    <button data-testid={`spk-delete-${doc.id}`} onClick={() => remove(doc)} className="h-7 px-2 flex items-center gap-1 text-xs rounded-md hover:bg-[#E31E24]/10 text-muted-foreground hover:text-[#E31E24] transition-colors ml-auto">
                      <Trash2 size={12} />Delete
                    </button>
                  )}
                </div>
                {historyFor === doc.id && (
                  <div className="border-t border-border pt-2 space-y-1" data-testid={`spk-history-list-${doc.id}`}>
                    {(doc.versions || []).slice().reverse().map((v) => (
                      <p key={v.version} className="text-[11px] text-muted-foreground" data-testid={`spk-version-entry-${doc.id}-${v.version}`}>
                        <span className="font-medium text-foreground">v{v.version}</span> — {v.data?.title} · {idr(v.data?.contract_value)} · edited by {v.edited_by} · {fmtDateTime(v.at)}
                      </p>
                    ))}
                    {(doc.history || []).slice().reverse().map((h, i) => (
                      <p key={i} className="text-[11px] text-muted-foreground">
                        <span className="font-medium text-foreground">{h.by_name}</span> {h.action.replace(/_/g, " ")} · {fmtDateTime(h.at)}{h.note ? ` — ${h.note}` : ""}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {dialog && <SpkBuilder project={project} initial={dialog === "create" ? null : dialog} users={users} onClose={() => setDialog(null)} onSaved={load} />}
      {reviewDoc && (
        <ReviewDialog open testid="spk-review" showProgress={false}
          title="Sign SPK"
          subtitle={`"${reviewDoc.title}" · ${idr(reviewDoc.contract_value)} · signed ${(reviewDoc.signatures || []).length}/${(reviewDoc.required_signers || []).length || 1}`}
          onClose={() => setReviewDoc(null)} onReview={review} />
      )}
      {previewDoc && <SpkPreviewDialog doc={previewDoc} onClose={() => setPreviewDoc(null)} />}
      {partyDoc && <PartyEditDialog doc={partyDoc} onClose={() => setPartyDoc(null)} onSaved={load} />}
      {settingsOpen && <CompanySettingsDialog onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}
